'use client';

import { Formik, Form } from 'formik';
import { useStore } from '@lib/context/store-context';
import { onSubmit } from '@modules/business/handles/handle-on-submit';
import { initialValues } from '@modules/business/validation/initialValues';
import { validationSchema } from '@modules/business/validation/validationSchema';
import { BusinessInfo } from 'types/business';
import GiftCardTemplate from '@modules/business/components/giftcard-template';
import { useEffect, useState } from 'react';
import { useCart } from 'medusa-react';
import { useAccount } from '@lib/context/account-context';
import { trackViewItem } from '@lib/util/google-analytics-utils';
import { CURRENCY, VARIANT_CARD_CONFIG } from '@lib/constants/variants';
import { CARD_PROGRAMS, SEARCH_PROVIDERS } from '@lib/constants';
import { useKarunaContext } from '@lib/context/kurana-context';
import { getBusinessHandle } from '@lib/data/business-handle';
import { getLogger } from '@lib/util/logging-util';

export type AjustedVariant = {
  id: string;
  title: string;
  prices: number;
  fee: number;
};

type Props = {
  business: BusinessInfo;
  variants: AjustedVariant[];
  customVariants: AjustedVariant[];
  card_program: string;
  searchBy?: string;
};

const GiftCardTemplateWrap = (props: Props) => {
  const logger = getLogger();
  const { updateProductDetails } = useKarunaContext();
  const { setLoading, addItem } = useStore();
  const { cart } = useCart();
  const { business, variants, customVariants, card_program, searchBy } = props;
  const { customer } = useAccount();
  const [isOptedOut, setIsOptedOut] = useState<boolean>(false);
  const [isClickSubmit, setIsClickSubmit] = useState<boolean>(false);
  const [isTextMethod, setIsTextMethod] = useState<boolean>(false);
  const [summaries, setSummaries] = useState<any>(null);
  const defaultVariant = variants?.find((item) => item.title === VARIANT_CARD_CONFIG);
  if (defaultVariant) {
    initialValues.variant = {
      id: defaultVariant?.id,
      title: defaultVariant?.title,
      price: defaultVariant?.prices,
      fee: defaultVariant?.fee,
    };
  }

  useEffect(() => {
    if (business && variants?.length > 0) {
      setLoading(false);
      let priceDefault = variants[0]?.prices;
      if (variants?.length > 3) {
        priceDefault = variants[2]?.prices;
      }
      trackViewItem({
        value: (priceDefault ?? 0) / 100,
        currency: CURRENCY,
        items: [
          {
            item_name: `${(priceDefault ?? 0) / 100} ${business?.name} Gift Card`,
            price: (priceDefault ?? 0) / 100,
          },
        ],
      });
    }
  }, [business, variants]);

  useEffect(() => {
    updateProductDetails(cart, business, initialValues.variant);

    initialValues.giftCardType = card_program;
    document.body.classList.add('bg-white');

    return () => {
      document.body.classList.remove('bg-white');
      sessionStorage.removeItem(CARD_PROGRAMS.KEY);
    };
  }, []);

  useEffect(() => {
    updateProductDetails(cart, business, initialValues.variant);
  }, [cart]);

  let retryInterval: any;
  const getSummaries = async () => {
    try {
      retryInterval = setInterval(async () => {
        const res = await getBusinessHandle(business.alias, searchBy);
        if (res?.summaries?.retrieved === true) {
          if (res?.summaries) {
            setSummaries(res.summaries);
          }
          clearInterval(retryInterval);
        }
      }, 5000);
    } catch (error) {
      logger.error('Error fetching summaries:', error);
    }
  };

  useEffect(() => {
    if (business?.summaries?.retrieved !== true && searchBy === SEARCH_PROVIDERS.YELP) {
      getSummaries();
    }
  }, [business, searchBy]);

  return (
    <div className="bg-white">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) =>
          onSubmit({
            business,
            values,
            addItem,
            cart,
            customer,
            setLoading,
            setIsOptedOut,
            setIsClickSubmit,
            isTextMethod,
          })
        }
      >
        <Form>
          <GiftCardTemplate
            business={business}
            variants={variants}
            customVariants={customVariants}
            isOptedOut={isOptedOut}
            isClickSubmit={isClickSubmit}
            setIsClickSubmit={setIsClickSubmit}
            setIsTextMethod={setIsTextMethod}
            summaries={summaries || business?.summaries}
          />
        </Form>
      </Formik>
    </div>
  );
};

export default GiftCardTemplateWrap;
